import { AutocompleteRenderInputParams } from '@mui/material'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import { AssigneeEntity } from '@omnea-app/core/blocks'
import { Group } from '@omnea-app/core/coreFrontendClient'
import { isValidEmail } from '@omnea-app/legacy/utils/general/isValidEmail'
import { isUuid } from '@omnea-app/legacy/utils/string/isUuid'
import { AssigneeTypeEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { AssigneeAutocompleteTagChip } from './components/AssigneeAutocompleteTagChip'
import { AssigneeAutocompleteTextField } from './components/AssigneeAutocompleteTextField'
import { BaseAssigneeAutocomplete, getAssigneeLabel } from './components/BaseAssigneeAutocomplete'
import { AssigneesError } from './types'

export interface AssigneeAutocompleteUncontrolledProps {
  onInputChange?: (newValue: string) => void
  values: AssigneeEntity[]
  options: AssigneeEntity[]
  onChange: (newValue: AssigneeEntity[]) => void
  title?: string
  label?: string | React.ReactNode
  required?: boolean
  disabled?: boolean
  error?: AssigneesError
  fullWidth?: boolean
  placeholder?: string
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode
  onBlur?: () => void
  helperText?: string
  inputValue?: string
  onAddNew?: () => void
  addNewText?: string
  groupBy?: string
  hideGroups?: boolean
  additionalOptions?: AssigneeEntity[]
  users?: User[]
  groups?: Group[]
  setAssigneesError?: (error: AssigneesError) => void
  isUsersLoading?: boolean
  isGroupsLoading?: boolean
  isFormTemplatesLoading?: boolean
  allowSingleAssigneeOnly?: boolean
  hideUuidInLabel?: boolean
  userServerSideFilter?: boolean
  initialOpenState?: boolean
}

export interface User {
  id: string
  firstName: string
  lastName: string
  email: string
  isArchived: boolean
}

export const AssigneeAutocompleteUncontrolled = ({
  options,
  fullWidth,
  values,
  inputValue,
  renderInput,
  onChange,
  onInputChange = () => {},
  error,
  disabled,
  placeholder,
  onBlur,
  helperText,
  label,
  required,
  title,
  onAddNew,
  addNewText,
  groupBy,
  hideGroups = false,
  additionalOptions,
  users,
  groups,
  setAssigneesError,
  isGroupsLoading = false,
  isUsersLoading = false,
  isFormTemplatesLoading = false,
  allowSingleAssigneeOnly = false,
  hideUuidInLabel = false,
  userServerSideFilter = false,
  initialOpenState = false,
}: AssigneeAutocompleteUncontrolledProps) => {
  const [isOpen, setIsOpen] = useState(initialOpenState)
  const { t } = useTranslation('workflow-builder')
  const handleChange = (_: unknown, newValues: (AssigneeEntity | string)[]) => {
    const newValuesEntities = newValues
      .map((item) => {
        if (typeof item === 'string') {
          return { value: item, type: AssigneeTypeEnum.EMAIL, id: uuidv4() }
        }

        return item
      })
      .filter((item) => item.type !== AssigneeTypeEnum.EMAIL || isValidEmail(item.value))
      .slice(allowSingleAssigneeOnly ? -1 : 0) // Keep only the last item if single selection is enforced

    onChange(newValuesEntities)
  }

  return (
    <BaseAssigneeAutocomplete<true>
      options={options}
      open={isOpen}
      setIsOpen={setIsOpen}
      multiple
      fullWidth={fullWidth}
      value={values}
      groupBy={groupBy}
      hideGroups={hideGroups}
      disabled={disabled}
      onChange={handleChange}
      onAddNew={onAddNew}
      addNewText={addNewText}
      hideUuidInLabel={hideUuidInLabel}
      loading={isUsersLoading || isGroupsLoading}
      inputValue={inputValue}
      onInputChange={onInputChange}
      userServerSideFilter={userServerSideFilter}
      renderTags={(tagValue, getTagProps) => {
        return tagValue.map((option, index) => {
          const isVariableQuestionOption = option.type === AssigneeTypeEnum.VARIABLE
          const label =
            getAssigneeLabel(option) +
            (isVariableQuestionOption && !(hideUuidInLabel && isUuid(option.id)) ? ` (${option.id})` : '')

          const isInvalidOption = validateOption({
            option,
            additionalOptions,
            groups,
            users,
            isLoading: isGroupsLoading || isUsersLoading || isFormTemplatesLoading,
          })

          if (isInvalidOption && setAssigneesError && error !== 'invalid_assignee') {
            setAssigneesError('invalid_assignee')
          }

          return (
            <AssigneeAutocompleteTagChip
              key={index}
              option={option}
              label={label}
              getTagProps={getTagProps}
              index={index}
              isValid={!isInvalidOption}
            />
          )
        })
      }}
      renderInput={
        renderInput
          ? (params) => renderInput(params)
          : (params) => (
              <AssigneeAutocompleteTextField
                {...params}
                label={label}
                required={required}
                placeholder={values?.length === 0 ? placeholder || t('config-assignee-placeholder') : ''}
                data-testid={`assignee-autocomplete-${title}`}
                onBlur={onBlur}
                error={error}
                helperText={helperText}
                onTabKeyDown={(newValue) => handleChange(null, [...values, newValue])}
              />
            )
      }
    />
  )
}

/**
 * @returns true if the option is invalid (e.g., email is not valid, or user/group/variable does not exist)
 */
const validateOption = ({
  option,
  additionalOptions,
  groups,
  users,
  isLoading,
}: {
  option: AssigneeEntity
  additionalOptions?: AssigneeEntity[]
  groups?: Group[]
  users?: User[]
  isLoading: boolean
}) => {
  if (isLoading) {
    return false
  }
  switch (option.type) {
    case AssigneeTypeEnum.VARIABLE:
      return Boolean(additionalOptions && !additionalOptions.some((additionalOption) => additionalOption.id === option.id))
    case AssigneeTypeEnum.GROUP:
      return Boolean(groups && !groups.some((group) => group.id === option.id))
    case AssigneeTypeEnum.USER:
      return Boolean(users && !users.some((user) => user.id === option.id))
    case AssigneeTypeEnum.EMAIL:
      return !isValidEmail(option.value)
    default:
      return false
  }
}
